import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { Typography, Badge, colors, IconGraphy, Tab, Grid } from '@leapeasy/ui-kit';
import { UserBuildingSearchTab } from './UserBuildingSearchTab';
import { useDispatch, useSelector } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  getLandlordTreeAction,
  getRMTreeAction,
  getVPTreeAction,
  setDelegationMoveOutAction,
} from 'store/actions/userActions';
import { TreeViewer } from './TreeViewer';
import { EditTreeViewModal } from './EditTreeViewModal';
import { useDemoMask } from 'hooks/useDemoMask';
import Copyright_icon from '../../../../assets/Treeview/copyright.svg';
import { DetailPageLoadingCenter } from 'components/DetailPageLoadingCenter';
import { GlobalUXState } from 'components/GlobalUXState';

const TREE_VIEW_TABS = [
  { id: 'tree', title: 'Tree View' },
  { id: 'search', title: 'User Property Search' },
];

export const TREEVIEW_SEARCH_TAB = 'search';

const BUILDING_SEARCH_QUERY_PARAMS = [
  'q',
  'search',
  'page',
  'pageSize',
  'sort',
  'propertyCount',
  'jobTitle',
  'invitedDate',
];

export const TreeviewBox = () => {
  const dispatch = useDispatch();
  const { mask } = useDemoMask();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = useMemo(() => {
    if (searchParams.get('tab') === TREEVIEW_SEARCH_TAB) return 1;
    if (searchParams.get('q')) return 1;
    return 0;
  }, [searchParams]);
  const [expandedById, setExpandedById] = useState({});
  const [hoveredCopyrightById, setHoveredCopyrightById] = useState({});
  const [openEditModal, setOpenEditModal] = useState(false);
  const [pendingMoves, setPendingMoves] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = currentUser?.role;

  const landlordTreeState = useSelector((state) => state.getIn(['user', 'landlord_tree']));
  const vpTreeState = useSelector((state) => state.getIn(['user', 'vp_tree']));
  const rmTreeState = useSelector((state) => state.getIn(['user', 'rm_tree']));
  const treeLoading = useSelector((state) => state.getIn(['user', 'treeloading']));
  const treeFetchError = useSelector((state) => state.getIn(['user', 'tree_fetch_error']));
  const isTreeInitialLoading = useMemo(() => {
    if (userRole === 'admin') return landlordTreeState == null;
    if (userRole === 'll') return vpTreeState == null;
    if (userRole === 'vp') return rmTreeState == null;
    return false;
  }, [userRole, landlordTreeState, vpTreeState, rmTreeState]);
  const isTreeBusy = Boolean(treeLoading) || isTreeInitialLoading;
  const landlordTree = useMemo(() => landlordTreeState?.toJS?.() || [], [landlordTreeState]);
  const vpTree = useMemo(() => vpTreeState?.toJS?.() || null, [vpTreeState]);
  const rmTree = useMemo(() => rmTreeState?.toJS?.() || null, [rmTreeState]);

  const treeRecords = useMemo(() => {
    if (userRole === 'admin') {
      return (landlordTree || []).map((item, index) => ({
        id: `landlord-${item?.userId || item?.landlordId || index}`,
        rootLabel: 'Operator',
        rootKey: 'll',
        editType: 'landlord',
        email: item?.email || '',
        data: item,
      }));
    }
    if (userRole === 'll' && vpTree) {
      return [
        {
          id: `vp-${vpTree?.userId || 'tree'}`,
          rootLabel: 'Operator',
          rootKey: 'll',
          editType: 'vp',
          email: vpTree?.email || currentUser?.email || '',
          data: vpTree,
        },
      ];
    }
    if (userRole === 'vp' && rmTree) {
      return [
        {
          id: `rm-${rmTree?.userId || 'tree'}`,
          rootLabel: 'Full Portfolio',
          rootKey: 'vp',
          editType: 'rm',
          email: rmTree?.email || currentUser?.email || '',
          data: rmTree,
        },
      ];
    }
    return [];
  }, [userRole, landlordTree, vpTree, rmTree, currentUser?.email]);

  const toggleExpanded = (recordId) => {
    setExpandedById((existing) => {
      const next = !existing[recordId];
      setHoveredCopyrightById((prev) => ({ ...prev, [recordId]: next }));
      return { ...existing, [recordId]: next };
    });
  };

  useEffect(() => {
    if (userRole === 'admin') {
      dispatch(getLandlordTreeAction());
    } else if (userRole === 'll') {
      dispatch(getVPTreeAction());
    } else if (userRole === 'vp') {
      dispatch(getRMTreeAction());
    }
  }, [dispatch, userRole]);

  useEffect(() => {
    if (!searchParams.get('q') || searchParams.get('tab') === TREEVIEW_SEARCH_TAB) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', TREEVIEW_SEARCH_TAB);
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const handleTabChange = useCallback(
    (index) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (index === 1) {
            next.set('tab', TREEVIEW_SEARCH_TAB);
          } else {
            next.delete('tab');
            BUILDING_SEARCH_QUERY_PARAMS.forEach((key) => next.delete(key));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const retryTreeFetch = useCallback(() => {
    if (userRole === 'admin') {
      dispatch(getLandlordTreeAction());
    } else if (userRole === 'll') {
      dispatch(getVPTreeAction());
    } else if (userRole === 'vp') {
      dispatch(getRMTreeAction());
    }
  }, [dispatch, userRole]);

  const onSaveTree = () => {
    if (!selectedRecord?.data?.landlordId) {
      setOpenEditModal(false);
      return;
    }

    const payload = {
      landlordId: selectedRecord.data.landlordId,
      moves: pendingMoves,
      editType: selectedRecord.editType,
    };

    dispatch(setDelegationMoveOutAction(payload));
    setOpenEditModal(false);
    setSelectedRecord(null);
    setPendingMoves([]);
  };

  return (
    <DashboardLayoutContainer>
      <Box sx={{ marginBottom: 2 }}>
        <Typography variant="h3" style={{ color: colors.purple[900] }}>
          Tree View
        </Typography>
      </Box>

      <Box padding="0 12px 24px" borderRadius="12px">
        <Grid style={{ marginBottom: 16 }}>
          {TREE_VIEW_TABS.map((tab, index) => (
            <Tab
              key={tab.id}
              title={tab.title}
              isSelected={selectedTab === index}
              onClick={() => handleTabChange(index)}
            />
          ))}
        </Grid>

        {selectedTab === 1 ? (
          <UserBuildingSearchTab />
        ) : treeFetchError ? (
          <GlobalUXState variant="apiError" actionLabel="Retry" onAction={retryTreeFetch} />
        ) : isTreeBusy ? (
          <DetailPageLoadingCenter label="Loading organization tree..." />
        ) : (
          <DndProvider backend={HTML5Backend}>
            <EditTreeViewModal
              open={openEditModal}
              onClose={() => {
                setOpenEditModal(false);
                setSelectedRecord(null);
                setPendingMoves([]);
              }}
              onSave={onSaveTree}
              treeRecord={selectedRecord}
              onMovesChange={setPendingMoves}
              loading={!!treeLoading}
            />
            <Box
              sx={{
                columnCount: {
                  xs: 1,
                  md: 2,
                  lg: 3,
                },
                columnGap: '16px',
              }}
            >
              {treeRecords.map((record) => {
                const expanded = !!expandedById[record.id];
                const hoveredCopyright = !!hoveredCopyrightById[record.id];
                return (
                  <Box
                    key={record.id}
                    style={{
                      position: 'relative',
                      width: '100%',
                      maxWidth: '100%',
                      display: 'inline-block',
                      breakInside: 'avoid',
                      WebkitColumnBreakInside: 'avoid',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontFamily: 'Arial, sans-serif',
                      margin: '0 0 16px 0',
                      backgroundColor: '#F8F4F952',
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        minWidth: 0,
                        borderBottom: '1px solid #e0e0e0',
                        padding: '14px 16px',
                        position: 'relative',
                        zIndex: '2',
                        ...(expanded ? { background: '#F8F4F9' } : {}),
                      }}
                    >
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          flex: '1 1 auto',
                          minWidth: 0,
                          overflow: 'hidden',
                        }}
                      >
                        <Box component="span" sx={{ flexShrink: 0 }}>
                          <Badge
                            background="#F3F1F4"
                            color="warmBlue"
                            label={record.rootLabel}
                            textSize="Large"
                            rounded
                          />
                        </Box>
                        <Typography
                          variant="body1"
                          title={mask('email', record.email) || undefined}
                          style={{
                            color: colors.neutral[900],
                            fontWeight: '400',
                            minWidth: 0,
                            flex: '1 1 auto',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {mask('email', record.email) || '-'}
                        </Typography>
                      </Box>
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: '5px',
                          flexShrink: 0,
                        }}
                      >
                        {expanded && (
                          <IconGraphy
                            icon="EditorLayout.EditNote"
                            width={16}
                            height={16}
                            style={{
                              color: colors.neutral[200],
                              border: 'solid 1px #e0e0e0',
                              padding: '6px',
                              borderRadius: '5px',
                              background: `${colors.purple[800]}`,
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              setSelectedRecord(record);
                              setOpenEditModal(true);
                            }}
                          />
                        )}
                        {hoveredCopyright ? (
                          <IconGraphy
                            icon={expanded ? 'Arrow.ExpandLess' : 'Arrow.ExpandMore'}
                            width={8}
                            height={8}
                            style={{
                              color: colors.neutral[900],
                              cursor: 'pointer',
                              border: 'solid 1px #e0e0e0',
                              padding: '10px',
                              borderRadius: '5px',
                              ...(expanded ? { background: '#E8D6EB' } : {}),
                            }}
                            onClick={() => toggleExpanded(record.id)}
                            onMouseLeave={() =>
                              setHoveredCopyrightById((prev) => ({
                                ...prev,
                                [record.id]: expanded || false,
                              }))
                            }
                          />
                        ) : (
                          <img
                            src={Copyright_icon}
                            alt="icon"
                            onMouseEnter={() =>
                              setHoveredCopyrightById((prev) => ({ ...prev, [record.id]: true }))
                            }
                          />
                        )}
                      </Box>
                    </Box>
                    {expanded && (
                      <Box>
                        <TreeViewer treeRecord={record} />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </DndProvider>
        )}
      </Box>
    </DashboardLayoutContainer>
  );
};
